import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import ErrorMessage from "../../components/StudioErstellen/ErrorMessage";
import classes from "./CreativeProfileEdit.module.css";
import { ChangeStudioModal } from "./ChangeStudioModal";
import { SmallModal } from "../../components/UI/SmallModal/SmallModal";
import AvatarEditor from 'react-avatar-editor';
import { useUpdateCreative } from "../../hooks/creative/useUpdateCreative";
import { useDeleteCreative } from "../../hooks/creative/useDeleteCreative";
import { useCreateStudioCreative } from "../../hooks/creative/useCreateStudioCreative";
import { useDeleteStudioCreative } from "../../hooks/creative/useDeleteStudioCreative";
import { usePublishCreative } from "../../hooks/creative/usePublishCreative";
import noUserImage from "../../public/fakeUser/noUser.webp";
import { useNavigate } from "react-router-dom";
import { saveCreativeProfileWithMedia } from "./creativeProfileMediaFlow";
import { notifyDeleteError, notifySaveError, notifyValidationToast } from "../../util/notifyActionError";

const ALLOWED_PROFILE_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/x-png']);
const ALLOWED_PROFILE_IMAGE_EXT = /\.(jpe?g|png)$/i;

function isAllowedProfileImageFile(file) {
  if (!file) return false;
  const fileType = (file.type || '').toLowerCase();
  if (fileType && ALLOWED_PROFILE_IMAGE_TYPES.has(fileType)) return true;
  return Boolean(file.name && ALLOWED_PROFILE_IMAGE_EXT.test(file.name));
}

const videoTags = [
  "Videografie", "Videography", "Kameraführung", "Camera Operation", "Content Creation", "Social Media Video", "Musikvideoproduktion", "Music Video Production", "Werbefilm", "Commercial Video", "Imagefilm", "Corporate Video", "Livestreaming", "Kurzfilm", "Short Film", "Dokumentarfilm", "Documentary", "Drohnenaufnahmen", "Drone Footage", "Behind-the-Scenes", "Farbkorrektur", "Color Grading", "Postproduktion", "Post Production", "VFX", "Visual Effects", "Storyboard", "Storyboarding", "Videoschnitt", "Video Editing", "Premiere Pro", "Final Cut Pro", "DaVinci Resolve"
];

const fotoTags = [
  "Fotografie", "Photography", "Portraitfotografie", "Portrait Photography", "Modefotografie", "Fashion Photography", "Eventfotografie", "Event Photography", "Produktfotografie", "Product Photography", "Hochzeitsfotografie", "Wedding Photography", "Architekturfotografie", "Architectural Photography", "Studiofotografie", "Studio Photography", "Streetfotografie", "Street Photography", "Reisefotografie", "Travel Photography", "Foodfotografie", "Food Photography", "Beautyfotografie", "Beauty Photography", "Editorialfotografie", "Editorial Photography", "Dokumentarfotografie", "Documentary Photography", "Tierfotografie", "Animal Photography", "Retusche", "Retouching", "Fotobearbeitung", "Photo Editing", "Portfolioerstellung", "Portfolio Curation"
];

const musicTags = [
  "Musikproduktion", "Music Production", "Beatproduktion", "Beatmaking", "Mixing", "Mastering", "Vocal Recording", "Audio Engineering", "Sounddesign", "Podcastproduktion", "Songwriting", "Arrangement", "Komposition", "MIDI Programmierung", "Synth Programmierung", "Studioaufnahme", "Vocal Editing", "Ableton", "Logic Pro", "FL Studio", "Foley", "Field Recording", "Samplepack Erstellung", "Musikcoaching", "Music Coaching"
];

export const CreativeProfileEdit = ({ creative, loading, error }) => {
  const navigate = useNavigate();
  const [ showChangeStudioModal, setShowChangeStudioModal ] = useState(false);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { handleUpdateCreative, loading: updateLoading } = useUpdateCreative();
  const { handleDeleteCreative, loading: deleteLoading } = useDeleteCreative();
  const { handleCreateStudioCreative, loading: createStudioCreativeLoading } = useCreateStudioCreative();
  const { handleDeleteStudioCreative, loading: deleteStudioCreativeLoading } = useDeleteStudioCreative();
  const { handlePublishCreative, loading: publishLoading } = usePublishCreative();
  const [isChecked, setIsChecked] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 742);
  const [editorSize, setEditorSize] = useState(200);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    profilePicture: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
  
  // Skills dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const skillsInputRef = useRef(null);
  const [hasSelectedSkills, setHasSelectedSkills] = useState(false);
  
  // Track changes
  const [formValues, setFormValues] = useState({
    displayName: '',
    jobTitle: '',
    description: '',
    skills: [],
    status: 'DRAFT',
    profilePicture: null
  });
  
  // Track intended status change (before save)
  const [intendedStatus, setIntendedStatus] = useState(null);
  
  // Track original studio ID to detect changes
  const [originalStudioId, setOriginalStudioId] = useState(null);
  const originalStudioIdInitialized = useRef(false);
  // Track which creative we've initialized form for (to avoid resetting on every update)
  const initializedCreativeIdRef = useRef(null);
  // Track previous status to detect changes
  const previousStatusRef = useRef(null);
  
  // Display profile picture (from formValues or original creative)
  const displayProfilePicture = formValues.profilePicture?.url || creative?.processedProfilePicture || creative?.user?.processedProfilePicture || noUserImage;
  
  const [displayNameError, setDisplayNameError] = useState('');
  const [errors, setErrors] = useState({
    profilePicture: '',
    displayName: '',
    jobTitle: '',
    description: '',
    skills: '',
    publish: ''
  });
  const [isDescriptionOverLimit, setIsDescriptionOverLimit] = useState(false);
  
  // Gesture state for editor
  const gestureStateRef = useRef({
    isPanning: false,
    lastPoint: null,
    pinchStartDistance: null,
    startScale: 1,
    startPosition: { x: 0.5, y: 0.5 }
  });
  
  // Handle window resize to detect mobile and update editor size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 742;
      setIsMobile(mobile);
      if (mobile) {
        setEditorSize(window.innerWidth);
      } else {
        setEditorSize(200);
      }
    };
    handleResize(); // Set initial size
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock body scroll when full-page editor is open on mobile
  useEffect(() => {
    if (isMobile && open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMobile, open]);

  // Initialize form values from creative (only when creative ID changes or on initial load)
  useEffect(() => {
    if (creative && creative.id !== initializedCreativeIdRef.current) {
      setFormValues({
        displayName: creative?.displayName || '',
        jobTitle: creative?.jobTitle || '',
        description: creative?.description || '',
        skills: creative?.skills || [],
        status: creative?.status || 'DRAFT',
        profilePicture: null
      });
      setIsChecked(creative?.status === 'PUBLISHED');
      setHasSelectedSkills((creative?.skills || []).length > 0);
      setIntendedStatus(null); // Reset intended status on new creative load
      // Store original studio ID to track changes (only set once on initial load)
      if (!originalStudioIdInitialized.current) {
        setOriginalStudioId(creative?.studios?.items?.[0]?.studioID || null);
        originalStudioIdInitialized.current = true;
      }
      initializedCreativeIdRef.current = creative.id;
      previousStatusRef.current = creative?.status;
    } else if (creative) {
      // Only sync isChecked if there's no pending status change
      // This prevents reverting when user toggles but hasn't saved yet
      if (intendedStatus === null) {
        const currentStatus = creative?.status;
        const previousStatus = previousStatusRef.current;
        
        if (currentStatus !== previousStatus) {
          const newIsChecked = currentStatus === 'PUBLISHED';
          setIsChecked(newIsChecked);
          previousStatusRef.current = currentStatus;
        }
      }
    }
  }, [creative, intendedStatus]);

  // Update hasSelectedSkills when skills change
  useEffect(() => {
    setHasSelectedSkills(formValues?.skills?.length > 0);
  }, [formValues.skills]);

  // Validate skills when they change
  useEffect(() => {
    const skills = formValues?.skills || [];
    if (skills.length < 3 && skills.length > 0) {
      // Only show error if user has started selecting but hasn't reached minimum
      setErrors(prev => {
        if (prev.skills) return prev; // Already has error, don't update
        return { ...prev, skills: 'Du musst mindestens 3 Skills & Services auswählen.' };
      });
    } else if (skills?.length >= 3) {
      // Clear error when valid
      setErrors(prev => {
        if (!prev.skills) return prev; // No error, don't update
        return { ...prev, skills: '' };
      });
    }
  }, [formValues.skills]);
  
  // Check if there are changes
  // Now includes status changes in hasChanges check
  const hasChanges = () => {
    if (!creative) {
      return false;
    }
    const originalSkills = creative.skills || [];
    const currentSkills = formValues.skills || [];
    // Create copies before sorting to avoid mutating read-only arrays
    const originalSkillsSorted = [...originalSkills].sort();
    const currentSkillsSorted = [...currentSkills].sort();
    const skillsChanged = JSON.stringify(originalSkillsSorted) !== JSON.stringify(currentSkillsSorted);
    
    // Check if studio has changed
    const currentStudioId = creative?.studios?.items?.[0]?.studioID || null;
    const studioChanged = currentStudioId !== originalStudioId;
    
    // Check if status has changed
    const currentStatus = creative?.status || 'DRAFT';
    const targetStatus = intendedStatus !== null ? intendedStatus : currentStatus;
    const statusChanged = intendedStatus !== null && intendedStatus !== currentStatus;
    
    const hasChangesResult = (
      formValues.displayName !== (creative.displayName || '') ||
      formValues.jobTitle !== (creative.jobTitle || '') ||
      formValues.description !== (creative.description || '') ||
      skillsChanged ||
      formValues.profilePicture !== null ||
      studioChanged ||
      statusChanged
    );
    
    return hasChangesResult;
  };

  // Get available tags based on creative type (memoized)
  const availableTags = useMemo(() => {
    const list = [];
    const creativeType = creative?.creativeType || '';

    if (creativeType === 'MUSIC') {
      list.push(...musicTags);
    } else if (creativeType === 'VISUAL') {
      list.push(...fotoTags, ...videoTags);
    }

    // Remove duplicates and sort once
    return [...new Set(list)].sort();
  }, [creative?.creativeType]);

  // Filter tags based on search query (memoized)
  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return availableTags;
    const query = searchQuery.toLowerCase();
    return availableTags.filter(tag => tag.toLowerCase().includes(query));
  }, [availableTags, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          skillsInputRef.current && !skillsInputRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchQuery("");
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Use backend's canBePublished field instead of checking lengths manually
  // Backend automatically sets this based on active packages/projects
  // Handle null values from backend (convert to false)
  const canBePublished = creative?.canBePublished ?? false;
  // Fallback: if canBePublished is null, check manually for active items
  const hasActivePackages = creative?.packages?.items?.some(p => p.isActive === true) ?? false;
  const hasActiveProjects = creative?.projects?.items?.some(p => p.status === 'PUBLISHED') ?? false;
  const finalCanBePublished = canBePublished || (hasActivePackages && hasActiveProjects);

  // Clear publish error when profile can be published
  useEffect(() => {
    if (finalCanBePublished) {
      setErrors(prev => {
        if (prev.publish) {
          return { ...prev, publish: '' };
        }
        return prev;
      });
    }
  }, [finalCanBePublished]);

  const handleToggle = () => {
    if (!isChecked) {
      // Publishing: Check if can be published
      if (!finalCanBePublished) {
        setErrors(prev => ({ ...prev, publish: 'Profil kann nicht veröffentlicht werden. Bitte aktives Projekt und Paket anlegen.' }));
        return;
      }
      
      // Clear publish error if it exists
      if (errors.publish) {
        setErrors(prev => ({ ...prev, publish: '' }));
      }
      
      // Just update local state - save will happen when user clicks "Änderungen speichern"
      setIsChecked(true);
      setIntendedStatus('PUBLISHED');
    } else {
      // Clear publish error when toggling off
      if (errors.publish) {
        setErrors(prev => ({ ...prev, publish: '' }));
      }
      
      // Just update local state - save will happen when user clicks "Änderungen speichern"
      setIsChecked(false);
      setIntendedStatus('INACTIVE');
    }
  };

  // Editor interaction helpers
  const PAN_DAMPING = 1.6;
  const PINCH_EXPONENT = 0.65;
  const WHEEL_STEP_IN = 1.02;
  const WHEEL_STEP_OUT = 0.98;
  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
  const clampPosition = (pos) => ({ x: clamp(pos.x, 0, 1), y: clamp(pos.y, 0, 1) });
  
  // Get current editor size (mobile uses full width, desktop uses 200)
  const getEditorSize = () => isMobile && open ? editorSize : 200;

  const handleWheel = (e) => {
    const delta = -e.deltaY;
    const factor = delta > 0 ? WHEEL_STEP_IN : WHEEL_STEP_OUT;
    setScale(prev => clamp(prev * factor, 1, 3));
  };

  const distanceBetween = (t1, t2) => {
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  };

  const midpoint = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2
  });

  const handleTouchStart = (e) => {
    const state = gestureStateRef.current;
    const container = editorContainerRef.current;
    if (!container) return;

    if (e.touches.length === 1) {
      state.isPanning = true;
      state.lastPoint = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      state.startPosition = position;
    } else if (e.touches.length === 2) {
      state.isPanning = true;
      state.pinchStartDistance = distanceBetween(e.touches[0], e.touches[1]);
      state.startScale = scale;
      state.startPosition = position;
      state.lastPoint = midpoint(e.touches[0], e.touches[1]);
    }
  };

  const handleTouchMove = (e) => {
    const state = gestureStateRef.current;
    const container = editorContainerRef.current;
    if (!container) return;

    if (e.touches.length === 1 && state.isPanning && state.lastPoint) {
      const current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      const dx = current.x - state.lastPoint.x;
      const dy = current.y - state.lastPoint.y;

      setPosition(prev => clampPosition({
        x: prev.x - dx / (getEditorSize() * PAN_DAMPING),
        y: prev.y - dy / (getEditorSize() * PAN_DAMPING)
      }));

      state.lastPoint = current;
    }

    if (e.touches.length === 2) {
      const newDist = distanceBetween(e.touches[0], e.touches[1]);
      if (state.pinchStartDistance) {
        const ratio = newDist / state.pinchStartDistance;
        const softened = Math.pow(ratio, PINCH_EXPONENT);
        setScale(clamp(state.startScale * softened, 1, 3));
      }
      const mid = midpoint(e.touches[0], e.touches[1]);
      const dx = mid.x - state.lastPoint.x;
      const dy = mid.y - state.lastPoint.y;
      setPosition(prev => clampPosition({
        x: prev.x - dx / (getEditorSize() * PAN_DAMPING),
        y: prev.y - dy / (getEditorSize() * PAN_DAMPING)
      }));
      state.lastPoint = mid;
    }
  };

  const handleTouchEnd = () => {
    gestureStateRef.current = {
      isPanning: false,
      lastPoint: null,
      pinchStartDistance: null,
      startScale: 1,
      startPosition: position
    };
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!isAllowedProfileImageFile(file)) {
        notifyValidationToast("Nur JPG, PNG und JPEG sind erlaubt", "Ungültiges Format");
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setFormData({
        ...formData,
        profilePicture: file
      });
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setOpen(true);
      };
      reader.onerror = () => {
        notifyValidationToast("Fehler beim Lesen der Datei");
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const closeModal = () => {
    setOpen(false);
    setPreviewImage(null);
    setFormData({ profilePicture: null });
    setScale(1);
    setPosition({ x: 0.5, y: 0.5 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSaveProfilePicture = () => {
    if (!formData.profilePicture) return;

    // Validate file type
    if (!isAllowedProfileImageFile(formData.profilePicture)) {
      notifyValidationToast("Nur JPG, PNG und JPEG sind erlaubt", "Ungültiges Format");
      return;
    }

    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();

      try {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
        
        canvas.toBlob((blob) => {
          if (!blob) {
            return;
          }
          const editedFile = new File(
            [blob],
            (formData.profilePicture?.name?.split('.').slice(0, -1).join('.') || 'profile') + '.jpg',
            { type: 'image/jpeg' }
          );

          const profilePictureData = {
            file: editedFile,
            url: dataUrl,
            fileName: editedFile.name,
            type: editedFile.type
          };

          setFormValues(prev => ({ ...prev, profilePicture: profilePictureData }));
          setErrors(prev => ({ ...prev, profilePicture: '' }));
          closeModal();
        }, 'image/jpeg', 0.92);
      } catch (e) {
        notifySaveError(e, {
          title: 'Profilbild konnte nicht gespeichert werden',
        });
        return;
      }
      return;
    }
    
    // Fallback: if no editor, use original file
    const reader = new FileReader();
    reader.onloadend = () => {
      const profilePictureData = {
        file: formData.profilePicture,
        url: reader.result,
        fileName: formData.profilePicture.name,
        type: formData.profilePicture.type
      };
      setFormValues(prev => ({ ...prev, profilePicture: profilePictureData }));
      setErrors(prev => ({ ...prev, profilePicture: '' }));
      closeModal();
    };
    reader.readAsDataURL(formData.profilePicture);
  };

  const validateDisplayName = (value) => {
    // Only letters, spaces, hyphens, and apostrophes
    if (value && !/^[a-zA-ZäöüÄÖÜß\s'-]+$/.test(value)) {
      return 'Nur Buchstaben erlaubt';
    }
    return '';
  };

  const handleDisplayNameChange = (e) => {
    const value = e.target.value;
    
    // Limit to 40 characters
    if (value.length > 40) {
      return;
    }

    // Validate and set error
    const error = validateDisplayName(value);
    setDisplayNameError(error);

    // Clear validation error when user types
    if (errors.displayName && !error) {
      setErrors(prev => ({ ...prev, displayName: '' }));
    }

    // Update form values if valid or empty
    if (!error || value === '') {
      setFormValues(prev => ({ ...prev, displayName: value }));
    }
  };

  const handleJobTitleChange = (e) => {
    const value = e.target.value;

    if (value && !/^[a-zA-ZäöüÄÖÜß\s'.,-]+$/.test(value)) {
      setErrors(prev => ({ ...prev, jobTitle: 'Nummern und Symbole sind nicht erlaubt' }));
      return;
    }

    if (value.length > 40) {
      setErrors(prev => ({ ...prev, jobTitle: 'Maximal 40 Zeichen erlaubt' }));
      return;
    }

    // Update form values if valid
    if (value.length <= 40) {
      setFormValues(prev => ({ ...prev, jobTitle: value }));
      // Clear validation error when user types
      if (errors.jobTitle) {
        setErrors(prev => ({ ...prev, jobTitle: '' }));
      }
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;

    // Hard limit to 500 characters, soft limit (warning) at 100 characters
    if (value.length > 500) {
      return;
    }

    setFormValues(prev => ({ ...prev, description: value }));
    setIsDescriptionOverLimit(value.length > 100);

    // Validate and set error
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, description: 'Du musst eine Beschreibung für deinen Service eingeben.' }));
    } else if (value.trim().length < 10) {
      setErrors(prev => ({ ...prev, description: 'Du musst mindestens 10 Zeichen für deine Beschreibung eingeben.' }));
    } else {
      // Clear validation error when valid
      setErrors(prev => {
        if (!prev.description) return prev;
        return { ...prev, description: '' };
      });
    }
  };

  // Handle tag selection
  const handleTagToggle = (tag) => {
    const currentSkills = formValues.skills || [];
    const isSelected = currentSkills.includes(tag);
    
    let newSkills;
    if (isSelected) {
      newSkills = currentSkills.filter(s => s !== tag);
    } else {
      newSkills = [...currentSkills, tag];
    }
    
    setFormValues(prev => ({ ...prev, skills: newSkills }));
    // Clear search input after selecting from the list
    if (!isSelected) {
      setSearchQuery("");
    }

    // Clear validation error when user selects/removes skills
    if (errors.skills && newSkills.length >= 3) {
      setErrors(prev => ({ ...prev, skills: '' }));
    }
  };

  // Handle tag removal from selected chips
  const handleTagRemove = (tag) => {
    const currentSkills = formValues.skills || [];
    const newSkills = currentSkills.filter(s => s !== tag);
    setFormValues(prev => ({ ...prev, skills: newSkills }));

    // Clear validation error when user removes skills if still valid
    if (errors.skills && newSkills.length >= 3) {
      setErrors(prev => ({ ...prev, skills: '' }));
    }
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    setIsDropdownOpen(true);
  };

  // Validation function
  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      displayName: '',
      jobTitle: '',
      description: '',
      skills: '',
      profilePicture: ''
    };

    // Validate displayName
    if (!formValues.displayName?.trim()) {
      newErrors.displayName = "Du musst einen Namen eingeben.";
      isValid = false;
    } else if (displayNameError) {
      newErrors.displayName = displayNameError;
      isValid = false;
    }

    // Validate jobTitle
    if (!formValues.jobTitle?.trim()) {
      newErrors.jobTitle = "Du musst einen Jobtitel eingeben.";
      isValid = false;
    }

    // Validate description
    if (!formValues.description?.trim()) {
      newErrors.description = "Du musst eine Beschreibung für deinen Service eingeben.";
      isValid = false;
    } else if (formValues.description?.trim().length < 10) {
      newErrors.description = "Du musst mindestens 10 Zeichen für deine Beschreibung eingeben.";
      isValid = false;
    }

    // Validate skills
    const skills = formValues.skills || [];
    if (skills.length < 3) {
      newErrors.skills = "Du musst mindestens 3 Skills & Services auswählen.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle save button click
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (!hasChanges()) {
      return;
    }

    try {
      await saveCreativeProfileWithMedia({
        creative,
        formValues,
        originalStudioId,
        intendedStatus,
        updateCreative: handleUpdateCreative,
        publishCreative: handlePublishCreative,
      });

      // Reset intended status after save
      setIntendedStatus(null);
      
      // Reset form values to reflect saved state
      setFormValues(prev => ({ ...prev, profilePicture: null }));
      
      // Navigate to overview with saved state
      navigate(`/creative-editing/${creative.id}/overview`, { state: { saved: true } });
    } catch (error) {
      if (error.cleanupError) {
        console.warn('Profilbild-Cleanup nach fehlgeschlagenem Creative-Update ist fehlgeschlagen:', error.cleanupError);
      }
      notifySaveError(error, { onRetry: () => handleSave() });
    }
  };

  // Handle studio selection from modal
  const handleStudioSelect = async (selectedStudioId) => {
    if (!creative?.id) return;

    try {
      // Get the current studio-creative link (if exists)
      const currentStudioLink = creative?.studios?.items?.[0];
      
      // Delete existing link if it exists
      if (currentStudioLink?.id) {
        try {
          await handleDeleteStudioCreative({ id: currentStudioLink.id });
        } catch (deleteError) {
          // Continue even if deletion fails (might not exist)
        }
      }

      // Create new link if a studio is selected
      if (selectedStudioId) {
        await handleCreateStudioCreative({
          studioID: selectedStudioId,
          creativeID: creative.id
        });
      }

      // The refetchQueries in the hooks will automatically update the creative data
      // After refetch, the creative prop will update and hasChanges() will detect the studio change
    } catch (error) {
      notifySaveError(error, {
        title: 'Studio konnte nicht geändert werden',
      });
    }
  };

  // Handle delete button click
  const handleDelete = async () => {
    if (!creative?.id) return;

    const confirmed = window.confirm('Möchtest du dein Kreativ-Profil wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.');
    if (!confirmed) return;

    try {
      await handleDeleteCreative({ id: creative.id });
      // Navigate back to services-studios page after successful deletion
      navigate('/services-studios');
    } catch (error) {
      notifyDeleteError(error, { onRetry: () => handleDelete() });
    }
  };

  if (loading) {
    return (
      <div className={classes.contentContainer}>
        <div className={classes.loadingContainer}>
          <p>Creative-Profil wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.contentContainer}>
        <div className={classes.errorContainer}>
          <p>Creative-Profil konnte nicht geladen werden.</p>
        </div>
      </div>
    );
  }

  if (!creative) {
    return (
      <div className={classes.contentContainer}>
        <div className={classes.errorContainer}>
          <p>Creative-Profil wurde nicht gefunden.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.creativeProfileEditPage}>
      <div className={classes.contentContainer}>
          <header className={classes.headerContainer}>
            <div className={classes.header}>
              <Link to={`/creative-editing/${creative?.id}`} className={classes.backButtonPhone}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>
              </Link>
              <Link to={`/creative-editing/${creative?.id}`} className={classes.backButton}>
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                  <path d="M6.33301 0.0712891L7.15039 0.888672L7.22168 0.958984L1.88281 6.29688L7.18652 12.041L7.25391 12.1143L7.18066 12.1826L6.33105 12.9668L6.25781 13.0342L6.18945 12.9609L0.0771484 6.33984L0.078125 6.33887L0.0712891 6.33301L0 6.2627L6.2627 0L6.33301 0.0712891Z" fill="#1F1F1F"/>
                </svg>
                <p className="semiboldSmall">Zurück</p>
              </Link>
              <button 
                className={`${classes.saveButton} ${hasChanges() ? classes.saveButtonActive : ''} mediumLarge`}
                onClick={handleSave}
                disabled={!hasChanges() || updateLoading}
              >
                {updateLoading ? 'Speichern...' : 'Änderungen speichern'}
              </button>
            </div>
          </header>
          <div className={classes.mainContent}>
            <h1 className="titleSemibold">Bearbeite dein Kreativ-Profil</h1>
            <div className={classes.profileContainer}>
              <div className={classes.activateContainer}>
                <header className={classes.activateHeader}>
                  <div className={classes.activateTitle}>
                    <h4 className="semiboldMiddle">Kreativ-Profil Status</h4>
                    <p className="regularXxs" style={{color: "var(--dark-grey-4)"}}>
                    Deaktivierte Profile werden im Studio nicht angezeigt und können nicht gebucht werden.
                    </p>
                  </div>
                  <div className={classes.switchContainer}>
                      <label className={classes.switch}>
                          <input 
                            type="checkbox" 
                            checked={isChecked} 
                            onChange={handleToggle}
                            disabled={updateLoading || publishLoading}
                          />
                          <span className={classes.slider}></span>
                      </label>
                  </div>
                </header>
                {errors.publish && (
                  <div style={{ marginTop: '12px' }}>
                    <ErrorMessage>{errors.publish}</ErrorMessage>
                  </div>
                )}
                {!finalCanBePublished && (
                <>
                  <div className={classes.horizontalLine}></div>
                  <div className={classes.unfinishedContainer}>
                    <p className={classes.unfinishedTitle}>Schließe die folgenden schritte ab, damit dein Kreativ-Profil aktiviert wird:</p>
                    <ul className={classes.processLinksContainer}>
                      <li className={classes.processLink}>
                        <div className={classes.processLinkContent}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <rect x="0.171429" y="0.171429" width="23.6571" height="23.6571" rx="11.8286" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.342857"/>
                                <line x1="7.17697" y1="12.1344" x2="10.0891" y2="15.1092" stroke="white" strokeWidth="0.888889"/>
                                <line x1="9.46713" y1="15.0998" x2="16.3328" y2="8.56951" stroke="white" strokeWidth="0.888889"/>
                            </svg>
                            <span className="regularSmall">Kreativ-Profil erstellen</span>
                        </div>
                    </li>
                    {/* Check for active projects: status === 'PUBLISHED' means active */}
                    {creative?.projects?.items?.some(p => p.status === 'PUBLISHED') ? (
                      <Link to={`/creative-editing/${creative?.id}/project`} className={classes.processLink}>
                          <div className={classes.processLinkContent}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <rect x="0.171429" y="0.171429" width="23.6571" height="23.6571" rx="11.8286" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.342857"/>
                                  <line x1="7.17697" y1="12.1344" x2="10.0891" y2="15.1092" stroke="white" strokeWidth="0.888889"/>
                                  <line x1="9.46713" y1="15.0998" x2="16.3328" y2="8.56951" stroke="white" strokeWidth="0.888889"/>
                              </svg>
                              <span className="regularSmall">Arbeiten hinzufügen</span>
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="14" viewBox="0 0 7 14" fill="none">
                              <path d="M0.3986 13.3203L6.38281 6.72166M6.36703 7.46886L0.382812 0.320312" stroke="#0B0418"/>
                          </svg>
                      </Link>
                    ) : (
                      <li className={classes.processLink}>
                        <div className={classes.processLinkContent}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" fill="white" stroke="#B1B6BB"/>
                            </svg>
                            <span className="regularSmall">Arbeiten hinzufügen</span>
                        </div>
                      </li>
                    )}
                    <Link to={`/creative-editing/${creative?.id}/package`} className={classes.processLink}>
                        <div className={classes.processLinkContent}>
                            {/* Check for active packages: isActive === true means active */}
                            {creative?.packages?.items?.some(p => p.isActive === true) ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <rect x="0.171429" y="0.171429" width="23.6571" height="23.6571" rx="11.8286" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.342857"/>
                                    <line x1="7.17697" y1="12.1344" x2="10.0891" y2="15.1092" stroke="white" strokeWidth="0.888889"/>
                                    <line x1="9.46713" y1="15.0998" x2="16.3328" y2="8.56951" stroke="white" strokeWidth="0.888889"/>
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" fill="white" stroke="#B1B6BB"/>
                                </svg>
                            )}
                            <span className="regularSmall">Pakete anbieten</span>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="14" viewBox="0 0 7 14" fill="none">
                            <path d="M0.3986 13.3203L6.38281 6.72166M6.36703 7.46886L0.382812 0.320312" stroke="#0B0418"/>
                        </svg>
                      </Link>
                    </ul>
                  </div>
                </>
                )}
              </div>
              <div className={classes.studioWrapper}>
                <h3 className="semiboldMiddle">Studio</h3>
                <div className={classes.studioInputContainer} onClick={() => setShowChangeStudioModal(true)}>
                  {creative?.studios?.items?.[0]?.studio?.processedImages?.[0] ? (
                    <div className={classes.studioImageContainer}>
                      <img src={creative.studios.items[0].studio.processedImages[0]} alt="studio" />
                    </div>
                  ) : (
                    <div className={classes.studioImageContainer}>
                      <span className="regularMiddle">Kein Studio</span>
                    </div>
                  )}
                  <span className="regularMiddle">Anderes Studio verbinden</span>
                </div>
                <div className={classes.addUserInfosInput}>
                    <label className="semiboldMiddle">Profilbild</label>
                    <div className={classes.addUserInfosInputImage} onClick={handleImageClick}>
                        <input 
                            ref={fileInputRef}
                            type="file" 
                            accept="image/*"
                            onChange={handleFileChange}
                            className={classes.addUserInfosInput}
                            style={{ display: 'none' }}
                        />
                        <div className={classes.addUserInfosImageContainer}>
                          {displayProfilePicture && (
                            <img src={displayProfilePicture} alt="User" />
                          )}
                        </div>
                        <span className="slimRegularLarge">Profilbild ändern</span>
                    </div>
                </div>
                <div className={classes.addUserInfosInput}>
                    <label className="semiboldMiddle">Gezeigter Name</label>
                    <div className={classes.addUserInfosInputTextContainer}>
                        <input 
                            type="text" 
                            className={`${classes.addUserInfosInputText} regularMiddle ${displayNameError ? classes.inputError : ''}`} 
                            placeholder="Name" 
                            value={formValues.displayName || ''} 
                            onChange={handleDisplayNameChange}
                            maxLength={40}
                        />
                        <div className={classes.inputFooter}>
                            {(errors.displayName || displayNameError) && (
                                <ErrorMessage>{errors.displayName || displayNameError}</ErrorMessage>
                            )}
                            <div className={`${classes.textCounter} slimRegularSmallText`}>
                                {(formValues.displayName?.length || 0)} / 40
                            </div>
                        </div>
                    </div>
                </div>
                <div className={classes.addUserInfosInput}>
                    <label className="semiboldMiddle">Jobtitel</label>
                    <div className={classes.addUserInfosInputTextContainer}>
                        <textarea 
                            className={`${classes.addUserInfosTextarea} regularMiddle ${errors.jobTitle ? classes.inputError : ''}`} 
                            placeholder="Beispiel: Videograf, Fotograf, Producer"
                            value={formValues.jobTitle || ''} 
                            onChange={handleJobTitleChange}
                            maxLength={40}
                        />
                        <div className={classes.inputFooter}>
                            {errors.jobTitle && (
                                <ErrorMessage>{errors.jobTitle}</ErrorMessage>
                            )}
                            <div className={`${classes.textCounter} slimRegularSmallText`}>
                                {(formValues.jobTitle?.length || 0)} / 40
                            </div>
                        </div>
                    </div>
                </div>
                <div className={classes.addUserInfosInput}>
                    <label className="semiboldMiddle">Beschreibung</label>
                    <div className={classes.addUserInfosInputTextContainer}>
                        <textarea 
                            className={`${classes.aboutServiceTextarea} regularMiddle ${errors.description ? classes.inputError : ''} ${isDescriptionOverLimit ? classes.textareaLimitExceeded : ''}`} 
                            placeholder="Beschreibe, wer du bist und was deine kreative Arbeit auszeichnet."
                            value={formValues.description || ''} 
                            onChange={handleDescriptionChange}
                            maxLength={500}
                        />
                        <div className={classes.inputFooter}>
                            {errors.description && (
                                <ErrorMessage>{errors.description}</ErrorMessage>
                            )}
                            {isDescriptionOverLimit && !errors.description && (
                                <ErrorMessage>Es sind maximal 100 Zeichen empfohlen.</ErrorMessage>
                            )}
                            <div className={`${classes.textCounter} slimRegularSmallText`}>
                                {Math.min(formValues.description?.length || 0, 100)} / 100
                            </div>
                        </div>
                    </div>
                </div>
                <div className={classes.addUserInfosInput}>
                    <label className="semiboldMiddle">Skills & Services</label>
                    <div className={classes.skillsInputWrapper}>
                        {hasSelectedSkills && (
                            <div className={classes.selectedTagsContainer}>
                                {(formValues.skills || []).map((tag) => (
                                    <div onClick={() => handleTagRemove(tag)} key={tag} className={classes.selectedTag}>
                                        <span className="regularMiddle">{tag}</span>
                                        <svg className={classes.removeTagButton} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M9 3L3 9M3 3L9 9" stroke="#77808A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div 
                            className={`${classes.aboutServiceSkillsInputContainer} ${hasSelectedSkills ? classes.hasSelectedTags : ''}`}
                        >
                            <svg className={classes.searchIcon} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M13.7311 12.3972C14.7427 11.0611 15.3035 9.41345 15.3035 7.72566C15.3035 3.466 11.8683 0 7.65177 0C3.43529 0 0 3.466 0 7.72566C0 11.9853 3.42527 15.4413 7.65177 15.4413C9.32435 15.4413 10.9569 14.8887 12.2889 13.864L18.0678 19.5L19.5 18.0232L13.7311 12.3972ZM7.89214 13.3818H7.65177C4.55701 13.3818 2.04314 10.84 2.04314 7.71561C2.04314 4.59119 4.55701 2.06955 7.65177 2.06955C10.7465 2.06955 13.2504 4.60124 13.2504 7.72566C13.2504 10.8501 10.8968 13.2512 7.89214 13.3818Z" fill="#77808A"/>
                            </svg>
                            <input 
                                ref={skillsInputRef}
                                className={classes.aboutServiceSkillsInput} 
                                type="text" 
                                placeholder="Skills & Services hinzufügen"
                                value={searchQuery}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                            />
                        </div>
                        {isDropdownOpen && (
                            <div ref={dropdownRef} className={classes.dropdownContainer}>
                                <div className={classes.dropdownContent}>
                                    {filteredTags.length > 0 ? (
                                        filteredTags.slice(0, 80).map((tag) => {
                                            const isSelected = (formValues.skills || []).includes(tag);
                                            return (
                                                <div
                                                    key={tag}
                                                    className={classes.dropdownOption}
                                                    onClick={() => handleTagToggle(tag)}
                                                >
                                                    <span className="slimRegularSmallText">{tag}</span>
                                                    <input
                                                        className={classes.tagCheckbox}
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleTagToggle(tag)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className={classes.noResults}>
                                            <span className="slimRegularSmallText">Keine Ergebnisse gefunden</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={classes.skillsFooter}>
                        {errors.skills && (
                            <ErrorMessage>{errors.skills}</ErrorMessage>
                        )}
                        <span className="slimRegularSmallText">Wähle mindestens 3 Skills & Services aus</span>
                    </div>
                </div>
                {errors.profilePicture && (
                    <ErrorMessage>{errors.profilePicture}</ErrorMessage>
                )}
                <div className={classes.deleteButtonContainer}>
                    <button 
                        className={classes.deleteButton}
                        onClick={handleDelete}
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? 'Löschen...' : 'Profil löschen'}
                    </button>
                </div>
            </div>
            </div>
          </div>
      </div>

      {/* Mobile full-page editor */}
      {isMobile && open && previewImage && (
        <div className={classes.fullPageEditorContainer}>
          <div className={classes.fullPageEditorContent}>
            <div
              className={classes.fullPagePreviewContainer}
              ref={editorContainerRef}
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
            >
              <AvatarEditor
                ref={editorRef}
                image={previewImage}
                width={editorSize}
                height={editorSize}
                border={0}
                borderRadius={editorSize}
                color={[255, 255, 255, 0]}
                scale={scale}
                rotate={0}
                position={position}
                onPositionChange={setPosition}
                style={{ width: '100%', height: '100%', borderRadius: '50%' }}
              />
            </div>
          </div>
          <footer className={classes.fullPageEditorFooter}>
            <button onClick={closeModal} className={`${classes.fullPageCancelButton} mediumLarge`}>Abbrechen</button>
            <button 
              onClick={handleSaveProfilePicture} 
              className={`${classes.fullPageSaveButton} mediumLarge`}
              disabled={!formData.profilePicture}
            >
              Speichern
            </button>
          </footer>
        </div>
      )}

      {/* Desktop modal */}
      {!isMobile && (
        <SmallModal
          open={open}
          onBackdropClick={closeModal}
          fromCreateService={true}
        >
          <div className={classes.modalContainer}>
            <header className={classes.modalHeader}>
              <h2 className='titleSemibold3'>Vorschau deines Profilbilds.</h2>
              <p className='regularXxs' style={{color: 'var(--dark-grey-4)'}}>
                Dein Gesicht sollte sichtbar sein – das schafft Vertrauen und Persönlichkeit. Ideal: JPG/PNG, mind. 300×300 px.
              </p>
            </header>
            <div className={classes.modalContent}>
              {previewImage && (
                <div className={classes.previewContainer}>
                  <div
                    className={classes.previewImageContainer}
                    ref={editorContainerRef}
                    onWheel={handleWheel}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                  >
                    <AvatarEditor
                      ref={editorRef}
                      image={previewImage}
                      width={200}
                      height={200}
                      border={0}
                      borderRadius={200}
                      color={[255, 255, 255, 0]}
                      scale={scale}
                      rotate={0}
                      position={position}
                      onPositionChange={setPosition}
                      style={{ width: '200px', height: '200px', borderRadius: '50%' }}
                    />
                  </div>
                </div>
              )}
            </div>
            <footer className={classes.modalFooter}>
              <button onClick={closeModal} className={`${classes.cancelButton} mediumLarge`}>Abbrechen</button>
              <button 
                onClick={handleSaveProfilePicture} 
                className={`${classes.saveButtonModal} mediumLarge`}
                disabled={!formData.profilePicture}
              >
                Speichern
              </button>
            </footer>
          </div>
        </SmallModal>
      )}
      {showChangeStudioModal && 
        <ChangeStudioModal 
          userID={creative?.userID} 
          creative={creative} 
          onClose={() => setShowChangeStudioModal(false)} 
          showChangeStudioModal={showChangeStudioModal}
          setShowChangeStudioModal={setShowChangeStudioModal}
          onStudioSelect={handleStudioSelect}
          selectedStudioId={creative?.studios?.items?.[0]?.studioID || null}
      />}
    </div>
  );
};

export default CreativeProfileEdit;
